import React, { useEffect, useState } from 'react';
import { Button, Chip } from '@mui/material';
import { TrialBalance as ITrialBalance } from '../../interfaces/accounting.interface';
import { AccountService } from '../../services/account.service';
import { useToast } from '../../context/ToastContext';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ddd',
    fontWeight: 600,
};

const tableCellStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #eee',
};

const TYPE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning'> = {
    ASSET: 'primary',
    LIABILITY: 'warning',
    EQUITY: 'secondary',
    REVENUE: 'success',
    EXPENSE: 'default',
};

const formatAmount = (value: number) =>
    value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = new Date();
const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
const todayStr = today.toISOString().split('T')[0];

const TrialBalance: React.FC = () => {
    const toast = useToast();
    const [report, setReport] = useState<ITrialBalance | null>(null);
    const [loading, setLoading] = useState(false);
    const [from, setFrom] = useState(firstOfMonth);
    const [to, setTo] = useState(todayStr);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const data = await AccountService.getTrialBalance(from, to);
            setReport(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to load trial balance');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0 }}>Trial Balance</h2>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <label style={{ fontWeight: 500 }}>
                    From:&nbsp;
                    <input
                        type="date"
                        value={from}
                        onChange={e => setFrom(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <label style={{ fontWeight: 500 }}>
                    To:&nbsp;
                    <input
                        type="date"
                        value={to}
                        onChange={e => setTo(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <Button variant="contained" onClick={fetchReport} disabled={loading}>
                    {loading ? 'Loading...' : 'Apply'}
                </Button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>Code</th>
                        <th style={tableHeaderStyle}>Account Name</th>
                        <th style={tableHeaderStyle}>Type</th>
                        <th style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Debits</th>
                        <th style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Credits</th>
                        <th style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Net Balance</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && (
                        <tr><td colSpan={6} style={{ ...tableCellStyle, textAlign: 'center' }}>Loading...</td></tr>
                    )}
                    {!loading && (!report || report.lines.length === 0) && (
                        <tr>
                            <td colSpan={6} style={{ ...tableCellStyle, textAlign: 'center', color: '#888' }}>
                                No posted entries in the selected period.
                            </td>
                        </tr>
                    )}
                    {!loading && report && report.lines.map((line, i) => (
                        <tr key={line.accountId} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={tableCellStyle}>{line.accountCode}</td>
                            <td style={tableCellStyle}>{line.accountName}</td>
                            <td style={tableCellStyle}>
                                <Chip label={line.accountType} size="small" color={TYPE_COLORS[line.accountType] ?? 'default'} />
                            </td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(line.totalDebits)}</td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(line.totalCredits)}</td>
                            <td style={{
                                ...tableCellStyle,
                                textAlign: 'right',
                                fontWeight: 500,
                                color: line.netBalance >= 0 ? '#2e7d32' : '#c62828',
                            }}>
                                {formatAmount(line.netBalance)}
                            </td>
                        </tr>
                    ))}
                    {!loading && report && report.lines.length > 0 && (
                        <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 700, borderTop: '2px solid #aaa' }}>
                            <td style={tableCellStyle} colSpan={3}>Grand Total</td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(report.grandTotalDebits)}</td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(report.grandTotalCredits)}</td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                                {formatAmount(report.grandTotalDebits - report.grandTotalCredits)}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default TrialBalance;
